/**
 * @file ShipmentPackagesEditor.tsx
 * @description Места отгрузки в сайдбаре «Новая отгрузка» (2026-09-22):
 * стартуют из мест расчёта доставки (вес и габариты, введённые в
 * калькуляторе), правятся тут же — карточки те же, что в калькуляторе
 * (`DeliveryPackageCard`), чтобы менеджер не учил второй экран.
 */

import { Plus } from "lucide-react";

import { DeliveryPackageCard } from "../carriers/DeliveryPackageCard";
import type { OrderDeliveryLineDto } from "../../lib/api-orders";
import {
  createPackageDraft,
  formatWeight,
  toNumber,
  type DeliveryPackageDraft,
} from "../../lib/delivery-quote";
import { Button } from "../ui/button";
import { SectionLabel } from "../ui/SectionLabel";

interface Props {
  value: DeliveryPackageDraft[];
  onChange: (next: DeliveryPackageDraft[]) => void;
  items: OrderDeliveryLineDto[];
  disabled?: boolean;
}

export function ShipmentPackagesEditor({ value, onChange, items, disabled }: Props) {
  const total = value.reduce((sum, p) => sum + (toNumber(p.weightG) ?? 0), 0);

  const placedElsewhere = (index: number): Map<string, number> => {
    const map = new Map<string, number>();
    value.forEach((p, i) => {
      if (i !== index) p.orderItemIds.forEach((id) => map.set(id, i + 1));
    });
    return map;
  };

  return (
    <section aria-label="Места" className="flex flex-col gap-2">
      <SectionLabel trailing={total > 0 ? `всего ${formatWeight(total)}` : "вес не указан"}>
        Места ({value.length})
      </SectionLabel>
      {value.map((p, index) => (
        <DeliveryPackageCard
          key={p.key}
          index={index}
          value={p}
          items={items}
          placedElsewhere={placedElsewhere(index)}
          disabled={disabled}
          onChange={(patch) =>
            onChange(value.map((x, i) => (i === index ? { ...x, ...patch } : x)))
          }
          onRemove={index === 0 ? undefined : () => onChange(value.filter((_, i) => i !== index))}
        />
      ))}
      <Button
        variant="outline"
        size="sm"
        className="min-h-11 self-start"
        disabled={disabled}
        onClick={() => onChange([...value, createPackageDraft()])}
      >
        <Plus className="mr-1 size-4" />
        Добавить место
      </Button>
    </section>
  );
}
